// Layer 3 — "Where this comes from". Every internal identifier in the product
// is here and nowhere else: the failure-mode code, the tier, the RRP Matrix
// row, and the detection signal that matched.
//
// COPY.md keeps "Tier 1" and "Tier 2" as vocabulary, on condition that each is
// always next to a one-line definition. That condition is met here rather than
// left to the reader's memory.

import { Disclosure, Label } from '../primitives';
import { Link } from 'react-router-dom';

import { Glossed } from '../Glossary';
import { contextFor, evidenceBadgeFor, EVIDENCE_BADGES } from '../../data/report';
import { EVIDENCE_CASES, formatCaseDate } from '../../data/evidence-cases';
import type { EvidenceCaseId } from '../../data/evidence-cases';
import type { Flag, Tier } from '../../types';

/** The one-line definition a tier is never shown without. */
export function tierDefinition(tier: Tier): string {
  return tier === 1
    ? EVIDENCE_BADGES['audit-precedent'].definition
    : EVIDENCE_BADGES['guidance-warning'].definition;
}

/**
 * What to say where a finding has no audit case attached.
 *
 * Silence would read as a missing citation. Which sentence is honest depends
 * on why there is none: recomputation needs no case, and a guidance warning
 * has none yet. Neither is the same as "we did not look".
 */
export function noCaseLine(finding: Pick<Flag, 'layer' | 'tier'>): string {
  if (finding.layer === 'deterministic') {
    return 'No audit case is attached, because none is needed: this finding is recomputation. The figures are in the document and the arithmetic is above.';
  }
  if (finding.tier === 2) {
    return 'No audit case is attached to this check yet. What it rests on is FEMP’s own guidance, which warns about this.';
  }
  return 'No audit case is attached to this finding.';
}

function CaseCard({ id }: { id: EvidenceCaseId }) {
  const c = EVIDENCE_CASES[id];
  return (
    <article className="fc__case">
      <div className="fc__case-head">
        <span className="fc__case-issuer">{c.issuer}</span>
        <span className="fc__case-number">
          {c.reportNumber ?? c.scale} · {formatCaseDate(c.date)}
        </span>
      </div>
      <p className="fc__case-summary">
        <Glossed>{c.summary}</Glossed>
      </p>
      <ul className="fc__incidents">
        {c.incidents.map((incident, i) => (
          <li key={i}>
            <span className="fc__incident-project">{incident.project}</span>
            {incident.amount ? (
              <span className="fc__incident-amount">{incident.amount}</span>
            ) : null}
            <span className="fc__incident-cause">
              <Glossed>{incident.cause}</Glossed>
            </span>
          </li>
        ))}
      </ul>
      <a
        className="fc__case-link"
        href={c.url}
        target="_blank"
        rel="noreferrer noopener"
      >
        Open the report ↗
      </a>
    </article>
  );
}

interface FindingProvenanceProps {
  finding: Flag;
  defaultOpen?: boolean;
}

/**
 * Layer 3's contents, apart from the disclosure that holds them.
 *
 * Split out for the same reason layer 2's body is: the printed review has no
 * disclosures, and it must print the layer this renders rather than a second
 * implementation of it.
 */
export function ProvenanceBody({ finding }: { finding: Flag }) {
  const context = contextFor(finding);
  const badge = evidenceBadgeFor(finding);
  const cases = finding.evidenceCaseIds
    .map((id) => EVIDENCE_CASES[id as EvidenceCaseId])
    .filter(Boolean);

  return (
    <>
      <div className="fc__block">
        <Label>Why this produces a dispute</Label>
        <p className="fc__prose">
          <Glossed>{context.whyItProducesDispute}</Glossed>
        </p>
      </div>

      <div className="fc__block">
        <Label>Audit cases</Label>
        {cases.length === 0 ? (
          <p className="fc__prose fc__prose--secondary">{noCaseLine(finding)}</p>
        ) : (
          <div className="fc__cases">
            {cases.map((c) => (
              <CaseCard key={c.id} id={c.id} />
            ))}
          </div>
        )}
      </div>

      {/* The detection signal the check actually matched on. Absent on a
          finding that carries none rather than rendered as an empty row. */}
      {finding.basis?.trim() ? (
        <div className="fc__block">
          <Label>What matched</Label>
          <p className="fc__prose fc__prose--mono">
            <Glossed>{finding.basis}</Glossed>
          </p>
        </div>
      ) : null}

      <div className="fc__block">
        <Label>RRP Matrix row</Label>
        <p className="fc__prose fc__prose--mono">
          <Glossed>{context.rrpLabel}</Glossed>
        </p>
        {context.whereItAppears ? (
          <p className="fc__where">
            <Glossed>{context.whereItAppears}</Glossed>
          </p>
        ) : null}
      </div>

      <div className="fc__block">
        <Label>This check</Label>
        <p className="fc__prose fc__prose--mono">
          <Link className="fc__fm-link" to="/checks">
            {finding.failureModeId}
          </Link>{' '}
          {context.failureModeName}
        </p>
        <p className="fc__where">
          Tier {finding.tier} — {tierDefinition(finding.tier)} Shown on this
          finding as “{badge.label}”.
        </p>
      </div>
    </>
  );
}

export default function FindingProvenance({
  finding,
  defaultOpen = false,
}: FindingProvenanceProps) {
  return (
    <Disclosure
      className="fc__layer fc__layer--provenance"
      summary="Where this comes from"
      defaultOpen={defaultOpen}
      panelClassName="fc__layer-body"
    >
      <ProvenanceBody finding={finding} />
    </Disclosure>
  );
}
